<script setup lang="ts">
import { computed, ref } from "vue";
import { useTaskStore } from "@/features/tasks/task.store";
import type { ITaskData, ITaskWorkType } from "@/features/tasks/task.types";
import type { IWorkType } from "@/features/work-types/work-type.types";
import CustomSearchDropdown from "@/components/custom/CustomSearchDropdown.vue";

const props = defineProps<{
  task: ITaskData;
  workTypes: IWorkType[];
  canEdit: boolean;
}>();

const emit = defineEmits<{
  (e: "updated"): void;
}>();

const taskStore = useTaskStore();

const selectedWorkTypeId = ref("");
const saving = ref(false);
const workTypeSearch = ref("");

const additionalWorkTypes = computed<ITaskWorkType[]>(() =>
  (props.task.work_types || []).filter((item) => !item.is_primary)
);

const availableWorkTypes = computed(() => {
  const existingIds = new Set(
    (props.task.work_types || []).map((item) => item.id)
  );

  existingIds.add(props.task.work_type_id);

  const query = workTypeSearch.value.trim().toLowerCase();

  return props.workTypes
    .filter((item) => item.is_active && !existingIds.has(item.id))
    .filter((item) => item.name.toLowerCase().includes(query));
});

const additionalWorkTypeOptions = computed(() =>
  availableWorkTypes.value.map((item) => ({
    value: item.id,
    name: item.name,
    app: String(item.base_points),
  }))
);

const editable = computed(
  () => props.canEdit && props.task.status === "in_progress"
);

async function addWorkType() {
  if (!editable.value || saving.value || !selectedWorkTypeId.value) {
    return;
  }

  const success = await taskStore.addTaskWorkType(
    saving,
    props.task.id,
    selectedWorkTypeId.value
  );

  if (success) {
    selectedWorkTypeId.value = "";
    emit("updated");
  }
}

async function removeWorkType(workTypeId: string) {
  if (!editable.value || saving.value) {
    return;
  }

  const success = await taskStore.removeTaskWorkType(
    saving,
    props.task.id,
    workTypeId
  );

  if (success) {
    emit("updated");
  }
}
</script>

<template>
  <div class="additional-work-types" @click.stop>
    <div v-if="additionalWorkTypes.length" class="additional-work-types__list">
      <div
        v-for="workType in additionalWorkTypes"
        :key="workType.id"
        class="additional-work-types__item"
      >
        <span>{{ workType.name }}</span>

        <button
          v-if="editable"
          type="button"
          class="additional-work-types__remove"
          :disabled="saving"
          title="Remove additional work type"
          @click.stop="removeWorkType(workType.id)"
        >
          ×
        </button>
      </div>
    </div>

    <div v-if="editable" class="additional-work-types__controls">
      <CustomSearchDropdown
        v-model="selectedWorkTypeId"
        :items="additionalWorkTypeOptions"
        label="Add work type..."
        position="bottom"
        dropdownFullWidth
        fullWidth
        clearable
        maxWidth="100%"
        :disabled="saving"
        @search="(value: string) => (workTypeSearch = value)"
      />

      <button
        type="button"
        class="additional-work-types__add"
        :disabled="saving || !selectedWorkTypeId"
        @click.stop="addWorkType"
      >
        {{ saving ? "..." : "+" }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.additional-work-types {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-top: 8px;
}

.additional-work-types__list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.additional-work-types__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border: 1px solid var(--color-border);
  border-radius: var(--base-radius-8);
  background: var(--color-secondary-system-background);
  color: var(--color-label);
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}

.additional-work-types__remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: var(--base-radius-4);
  background: transparent;
  color: var(--color-label-secondary);
  font-size: 17px;
  cursor: pointer;
}

.additional-work-types__remove:hover {
  background: var(--color-red-light);
  color: var(--color-system-red);
}

.additional-work-types__controls {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
}

.additional-work-types__controls :deep(.c-cu-table-dropdown) {
  flex: 1;
  min-width: 0;
}

.additional-work-types__select:hover,
.additional-work-types__select:focus {
  border-color: var(--color-primary);
}

.additional-work-types__add {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: none;
  border-radius: var(--base-radius-8);
  background: var(--color-primary);
  color: var(--color-white);
  font-size: 18px;
  font-weight: 500;
  cursor: pointer;
}

.additional-work-types__add:hover:not(:disabled) {
  background: var(--color-primary-disabled);
}

.additional-work-types__add:disabled,
.additional-work-types__remove:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>
